import React from 'react';
import { Tooltip } from 'antd';
import { toMinutes, formatTimeShort, formatTimeRange, counterGenderLabel, counterSeatsPerStep } from './helpers';

const axisFor = (counters) => {
    const starts = counters.map(c => toMinutes(c.startTime)).filter(v => v !== null);
    const ends = counters.map(c => toMinutes(c.endTime)).filter(v => v !== null);
    if (!starts.length || !ends.length) return null;
    const from = Math.floor(Math.min(...starts) / 60) * 60;
    const to = Math.max(Math.ceil(Math.max(...ends) / 60) * 60, from + 60);
    const span = to - from;
    const step = span <= 8 * 60 ? 60 : span <= 14 * 60 ? 120 : 180;
    const ticks = [];
    for (let m = from; m <= to; m += step) ticks.push(m);
    return { from, to, span, ticks };
};

const minutesToTime = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const minutesLabel = (m) => formatTimeShort(minutesToTime(m));

const CounterTimeline = ({ counters, slotMinutes, onSelect }) => {
    const axis = axisFor(counters);
    if (!axis) return null;
    const pos = (m) => `${((m - axis.from) / axis.span) * 100}%`;

    const length = slotMinutes > 0 ? slotMinutes : 60;
    const steps = [];
    for (let m = axis.from; m + length <= axis.to; m += length) steps.push(m);
    const seats = counterSeatsPerStep(counters, steps, length);
    const labelEvery = steps.length > 24;

    return (
        <div className="ss-ctl">
            <div className="ss-ctl-row ss-ctl-axis">
                <span />
                <div className="ss-ctl-track is-axis">
                    {axis.ticks.map(m => (
                        <span key={m} className="ss-ctl-tick" style={{ left: pos(m) }}>{minutesLabel(m)}</span>
                    ))}
                </div>
            </div>
            {counters.map(counter => {
                const start = toMinutes(counter.startTime);
                const end = toMinutes(counter.endTime);
                const breakFrom = toMinutes(counter.breakFrom);
                const breakTo = toMinutes(counter.breakTo);
                const parts = breakFrom !== null && breakTo !== null && breakFrom > start && breakTo < end
                    ? [[start, breakFrom], [breakTo, end]]
                    : [[start, end]];
                const tone = counter.isActive ? `is-${(counter.gender || 'both').toLowerCase()}` : 'is-inactive';
                const tip = `${counter.name} · ${counterGenderLabel(counter.gender)} · ${formatTimeRange(counter.startTime, counter.endTime)}`
                    + (breakFrom !== null && breakTo !== null ? ` · break ${formatTimeRange(counter.breakFrom, counter.breakTo)}` : '')
                    + (counter.isActive ? '' : ' · inactive');
                return (
                    <Tooltip key={counter.id} title={`${tip} — click to edit`}>
                        <button type="button" className={`ss-ctl-row ${tone}`} onClick={() => onSelect(counter)} aria-label={`Edit ${tip}`}>
                            <span className="ss-ctl-name">
                                <strong>{counter.name}</strong>
                                <span>{counter.isActive ? counterGenderLabel(counter.gender) : 'Inactive'}</span>
                            </span>
                            <div className="ss-ctl-track">
                                {axis.ticks.map(m => <span key={m} className="ss-ctl-grid" style={{ left: pos(m) }} />)}
                                {parts.map(([from, to]) => (
                                    <span key={from} className="ss-ctl-bar" style={{ left: pos(from), width: `calc(${pos(to)} - ${pos(from)})` }} />
                                ))}
                            </div>
                        </button>
                    </Tooltip>
                );
            })}
            <div className="ss-ctl-row ss-ctl-seats-row">
                <span className="ss-ctl-name">
                    <strong>Seats</strong>
                    <span>per {length}-min slot</span>
                </span>
                <div className="ss-ctl-track is-seats">
                    {steps.map((m, index) => {
                        const count = seats[index];
                        const showNumber = !labelEvery || index === 0 || seats[index - 1] !== count;
                        return (
                            <Tooltip key={m} title={`${formatTimeRange(minutesToTime(m), minutesToTime(m + length))}: ${count} seat${count === 1 ? '' : 's'}`}>
                                <span className={`ss-ctl-seat ${count ? '' : 'is-zero'}`}
                                    style={{ left: pos(m), width: `calc(${pos(m + length)} - ${pos(m)})`, '--level': Math.min(count, 6) }}>
                                    {showNumber ? count : ''}
                                </span>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CounterTimeline;
